/**
 * A card around one chart or list (the kit's TableCard frame): its title with the metric's
 * meaning, a line under it, and two actions every report has: "Show as table" (the same numbers
 * as a table, the accessible twin of the drawing) and "Download CSV" (the same columns, raw).
 * With nothing to show it says why instead of drawing zeros.
 */

import { useState } from "react";
import Button from "@web-ui/components/Button";
import DataTable from "@web-ui/components/DataTable";
import DataTableRow from "@web-ui/components/DataTableRow";
import TableCard from "@web-ui/components/TableCard";
import { csvName, downloadCsv, toCsv, type Table } from "../csv";
import type { Definition } from "../definitions";
import { MetricLabel } from "./metric-label";

export function ReportCard<T>({ title, description, table, file, empty, tableOnly, actions, children }: {
  /** A plain title, or a metric (its name and meaning). */
  title: string | Definition;
  description?: React.ReactNode;
  table: Table<T>;
  /** The CSV's name: brand, what and range (csv.ts csvName). */
  file: { brand: string; what: string; range?: { from: string; to: string } };
  /** Shown instead of the chart and every action when there is nothing to show. */
  empty?: React.ReactNode;
  /** The report is a table: no chart, no toggle. */
  tableOnly?: boolean;
  /** More controls before the two actions (a metric switch). */
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const [asTable, setAsTable] = useState(false);
  const label = typeof title === "string" ? title : title.label;
  const showTable = tableOnly || asTable;
  return (
    <TableCard
      title={<span className="text-sub-title-1">{typeof title === "string" ? title : <MetricLabel definition={title} />}</span>}
      description={description}
      actions={empty ? undefined : (
        <>
          {actions}
          {!tableOnly ? <Button type="button" variant="ghost" size="sm" aria-pressed={asTable} onClick={() => setAsTable(!asTable)}>{asTable ? "Show as chart" : "Show as table"}</Button> : null}
          <Button type="button" variant="outline" size="sm" icon="arrow-down" onClick={() => downloadCsv(csvName(file.brand, file.what, file.range), toCsv(table))}>Download CSV</Button>
        </>
      )}
    >
      {empty ?? (showTable ? <ReportTable label={label} table={table} /> : children)}
    </TableCard>
  );
}

/** The numbers as a table, as the screen words them. */
export function ReportTable<T>({ label, table }: { label: string; table: Table<T> }) {
  const columns = table.columns.filter((c) => !c.csvOnly);
  return (
    <DataTable label={label} cellsThead={<>{columns.map((c) => <th key={c.label} className={c.numeric ? "text-end" : undefined}>{c.tooltip ? <MetricLabel definition={{ label: c.label, tooltip: c.tooltip }} /> : c.label}</th>)}</>}>
      {table.rows.map((row, i) => (
        <DataTableRow key={i}>
          {columns.map((c) => {
            const value = c.value(row);
            const text = c.format ? c.format(value, row) : value ?? "—";
            const to = c.href?.(row);
            return <td key={c.label} className={`text-body-2 ${c.numeric ? "text-end tabular-nums" : ""}`}>{to ? <a className="font-medium hover:underline focus-visible:underline" href={to}>{text}</a> : text}</td>;
          })}
        </DataTableRow>
      ))}
    </DataTable>
  );
}
